/**
 * PaperlessCore API — shared fetch core (Spec 059, E3).
 *
 * Bundles the four private building blocks every domain module needs:
 * `getAuthHeaders`, `fetchWithErrorHandling`, `fetchJson`, `fetchVoid`, plus `baseUrl` and the
 * token-injection hook for tests. Moved bitgleich from the former monolithic `api.ts` — no
 * behavior change (E5).
 */

// Runtime configuration from window.__APP_CONFIG__ (injected at container startup)
// Falls back to import.meta.env for local development
declare global {
  interface Window {
    __APP_CONFIG__?: {
      apiUrl: string;
      googleClientId: string;
      oauthRedirectUri: string;
      appVersion?: string;
      enableStateCatalog?: string;
    };
  }
}

const API_BASE_URL =
  window.__APP_CONFIG__?.apiUrl || import.meta.env.VITE_API_URL || 'http://localhost:3000';

/** Domain-crossing types (E4): used by more than one domain module. */
export interface PaginatedResponse<T> {
  items: T[];
  pagination: {
    nextCursor?: string;
    prevCursor?: string;
    hasMore: boolean;
    pageSize: number;
  };
}

export interface ProblemDetails {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  traceId?: string;
  errorCode?: string;
  extensions?: { errorCode?: string; traceId?: string };
  /** Structured failure reason (e.g. ShareLinkGoneDto.reason on 410, Spec 015 E3). */
  reason?: string;
}

/**
 * Shared fetch core, passed into every domain module's constructor (E3).
 */
export class ApiCore {
  baseUrl: string;

  /**
   * Injected token (for tests and token refresh). When set, takes precedence over
   * localStorage so tests can supply a fake ID token without touching global storage.
   * @internal
   */
  idToken: string | null = null;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  /**
   * Get auth headers with JWT token.
   * Uses this.idToken when set (e.g. injected by tests), otherwise falls back to
   * localStorage 'auth_token' (runtime path).
   */
  getAuthHeaders(): HeadersInit {
    const token = this.idToken ?? localStorage.getItem('auth_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  /**
   * Central fetch wrapper with global error handling.
   * Handles 401 (logout + redirect), 5xx (toast), and network errors (toast).
   * Returns the Response object on success (2xx or accepted status codes).
   */
  async fetchWithErrorHandling(
    url: string,
    init?: RequestInit,
    suppressGlobalErrorHandler = false,
    // Spec 015 E2: a *public* fetch (e.g. the share-link viewer) suppresses even the
    // 401 global-handler bounce (logout + redirect to /login), because it renders its
    // own "please log in" state. Chunked upload (#279 Finding A) does NOT set this —
    // an expired session there must still force re-auth via the global handler.
    suppressAuthRedirect = false,
  ): Promise<Response> {
    const { handleApiError, ApiError } = await import('./errorHandler');

    let response: Response;
    try {
      response = await fetch(url, init);
    } catch (err) {
      // Network error (no response at all). In suppress mode, surface a typed error to the
      // caller (e.g. chunked upload records it as skipped) WITHOUT a global toast (#279 Finding A).
      if (suppressGlobalErrorHandler) {
        throw err instanceof Error ? err : new Error('Network error');
      }
      // handleApiError always throws
      return handleApiError(err);
    }

    if (!response.ok) {
      // Try to extract error message from RFC 7807 ProblemDetails response
      let message = response.statusText;
      let errorCode: string | undefined;
      let reason: string | undefined;
      try {
        const body: ProblemDetails = await response.json();
        message = body.detail || body.title || message;
        errorCode = body.extensions?.errorCode ?? body.errorCode;
        reason = body.reason;
      } catch {
        // Response body not parseable, use statusText
      }

      const apiError = new ApiError(
        message,
        response.status,
        response.statusText,
        errorCode,
        reason,
      );
      // 401 ALWAYS goes through the global handler (logout + redirect), even when suppressing
      // other statuses — a chunk hitting an expired session must still trigger re-auth (#279
      // Finding A) — UNLESS the caller explicitly suppresses the auth redirect too (E2).
      const suppressThisError =
        suppressGlobalErrorHandler && (response.status !== 401 || suppressAuthRedirect);
      if (suppressThisError) {
        throw apiError;
      }
      return handleApiError(apiError, response);
    }

    return response;
  }

  /**
   * Fetch JSON with global error handling. For endpoints returning a body.
   * Pass suppressGlobalErrorHandler=true to skip the global error toast/redirect for non-401
   * failures (the caller handles the error itself, e.g. chunked upload partial-failure summary).
   * Pass suppressAuthRedirect=true to also suppress the 401 logout/redirect bounce (Spec 015 E2,
   * e.g. the public share-link viewer, which renders its own "please log in" state).
   */
  async fetchJson<T>(
    url: string,
    init?: RequestInit,
    suppressGlobalErrorHandler = false,
    suppressAuthRedirect = false,
  ): Promise<T> {
    const response = await this.fetchWithErrorHandling(
      url,
      init,
      suppressGlobalErrorHandler,
      suppressAuthRedirect,
    );
    return response.json();
  }

  /**
   * Fetch with global error handling for void endpoints (204, DELETE, PATCH).
   * Accepts 2xx and 204 as success. Same suppress flags as `fetchJson` (Spec 086/F2).
   */
  async fetchVoid(
    url: string,
    init?: RequestInit,
    suppressGlobalErrorHandler = false,
    suppressAuthRedirect = false,
  ): Promise<void> {
    await this.fetchWithErrorHandling(url, init, suppressGlobalErrorHandler, suppressAuthRedirect);
  }
}
